import { useEffect, useState } from 'react'
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Flash } from '../components/Flash'
import { useOpenNotification } from '../components/NotificationBell'
import { ReviewPager, StarsRow } from '../components/ReviewTimeline'
import {
  api,
  formatVnd,
  type AccountSummary,
  type BalanceChange,
  type MyReview,
  type Order,
  type QueueRow,
  type UserNotification,
} from '../lib/api'

export type Tone = 'ok' | 'warn' | 'danger' | 'muted'

const ORDER_STATUS: Record<string, [string, Tone]> = {
  da_thanh_toan: ['Đã giao hàng', 'ok'],
  da_giao_hang: ['Đã giao hàng', 'ok'],
  cho_giao_hang: ['Chờ giao hàng', 'warn'],
  cho_van_chuyen: ['Chờ vận chuyển', 'warn'],
  chua_thanh_toan: ['Chưa thanh toán', 'danger'],
  huy_bo: ['Đã hủy bỏ', 'danger'],
  da_hoan_tien: ['Đã hoàn tiền', 'muted'],
}

const QUEUE_STATUS: Record<string, [string, Tone]> = {
  cho_xu_li: ['Chờ xử lí', 'warn'],
  thanh_cong: ['Thành công', 'ok'],
  that_bai: ['Thất bại', 'danger'],
}

const REVIEW_STATUS: Record<string, [string, Tone]> = {
  chap_nhan: ['Đã duyệt', 'ok'],
  cho_xu_li: ['Chờ duyệt', 'warn'],
  huy_bo: ['Từ chối', 'danger'],
}

export function StatusBadge({
  status,
  map = ORDER_STATUS,
}: {
  status: string
  map?: Record<string, [string, Tone]>
}) {
  const [label, tone] = map[status] || ['Không xác định', 'muted']
  return <span className={`acc-badge acc-badge-${tone}`}>{label}</span>
}

export function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} · ${p(d.getHours())}:${p(d.getMinutes())}`
}

const MENU = [
  { to: '/account', label: 'Đơn hàng', icon: 'fa-key', end: true },
  { to: '/account/dat-ho', label: 'Đặt hộ', icon: 'fa-cart-shopping' },
  { to: '/account/thong-bao', label: 'Thông báo', icon: 'fa-bell' },
  { to: '/account/khac', label: 'Tài khoản', icon: 'fa-user-gear' },
]

export function AccountLayout() {
  const { user, loading } = useAuth()
  const [summary, setSummary] = useState<AccountSummary | null>(null)

  useEffect(() => {
    if (!user) return
    api
      .accountSummary()
      .then((r) => setSummary(r.summary))
      .catch(() => setSummary(null))
  }, [user])

  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />

  const n = (v: number | undefined) => (v ?? 0).toLocaleString('vi-VN')

  return (
    <div className="acc">
      <div className="acc-stats">
        <div className="acc-stat acc-stat-wallet">
          <div className="acc-stat-top">
            <h3>Số dư</h3>
            <Link className="acc-stat-cta" to="/deposit">
              <i className="fa-solid fa-plus" aria-hidden /> Nạp tiền
            </Link>
          </div>
          <p className="acc-stat-line">
            <i className="fa-solid fa-coins" aria-hidden />
            <strong>{n(summary?.balance ?? user.balance)}</strong> xu
          </p>
          <p className="acc-stat-line">
            <i className="fa-regular fa-gem" aria-hidden />
            <strong>{n(summary?.balance2 ?? user.balance2)}</strong> gem
          </p>
        </div>
        <div className="acc-stat">
          <h3>
            <i className="fa-solid fa-bag-shopping" aria-hidden /> Đơn đã mua
          </h3>
          <p className="acc-stat-value">{summary ? n(summary.orders_done) : '…'}</p>
        </div>
        <div className="acc-stat">
          <h3>
            <i className="fa-solid fa-money-bill-wave" aria-hidden /> Đã nạp
          </h3>
          <p className="acc-stat-value">{summary ? formatVnd(summary.deposited) : '…'}</p>
        </div>
        <div className="acc-stat">
          <h3>
            <i className="fa-solid fa-medal" aria-hidden /> Cấp bậc
          </h3>
          <p className="acc-stat-value">
            {summary ? (
              <span className={`acc-tier acc-tier-${summary.tier.toLowerCase()}`}>{summary.tier}</span>
            ) : (
              '…'
            )}
          </p>
        </div>
      </div>

      <div className="acc-workspace">
        <nav className="acc-nav" aria-label="Menu hồ sơ">
          <h2>Hồ sơ của bạn</h2>
          <ul>
            {MENU.map((m) => (
              <li key={m.to}>
                <NavLink
                  to={m.to}
                  end={m.end}
                  className={({ isActive }) => `acc-nav-item${isActive ? ' active' : ''}`}
                >
                  <i className={`fa-solid ${m.icon}`} aria-hidden /> {m.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <section className="acc-main">
          <Outlet />
        </section>
      </div>
    </div>
  )
}

export function usePaged<T>(
  load: (page: number) => Promise<{ items: T[]; total_pages: number }>,
  reloadKey: unknown = 0,
) {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<T[]>([])
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    load(page)
      .then((r) => {
        if (!alive) return
        setItems(r.items)
        setTotalPages(r.total_pages || 1)
      })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, reloadKey])

  return { page, setPage, items, totalPages, loading }
}

function orderAction(o: Order) {
  if (o.status === 'chua_thanh_toan') {
    return (
      <Link className="acc-action acc-action-warn" to={`/checkout/${o.id}`}>
        <i className="fa-solid fa-wallet" aria-hidden /> Thanh toán
      </Link>
    )
  }
  if (o.type === 'buyitforme' || o.type === 'cape') return null
  if (o.status === 'huy_bo' || o.status === 'da_hoan_tien') return null
  return (
    <Link className="acc-action" to={`/account/don/${o.id}`}>
      <i className="fa-solid fa-key" aria-hidden /> Nhận key
    </Link>
  )
}

export function AccountOrdersPage() {
  const { page, setPage, items, totalPages, loading } = usePaged<Order>((p) =>
    api.orders(p, 10).then((r) => ({ items: r.orders, total_pages: r.total_pages })),
  )

  return (
    <>
      <div className="acc-head">
        <h2>Tất cả đơn hàng</h2>
      </div>
      {loading && !items.length ? <p className="muted">Đang tải…</p> : null}
      {!loading && !items.length ? (
        <div className="acc-empty">
          <p className="muted">Chưa có đơn hàng.</p>
          <Link className="btn btn-primary" to="/mua-minecraft">
            <i className="fa-solid fa-cart-shopping" aria-hidden /> Mua key Minecraft
          </Link>
        </div>
      ) : null}
      <ol className="acc-timeline">
        {items.map((o) => (
          <li key={o.id} className="acc-tl-item">
            <time dateTime={o.created_at}>{formatDateTime(o.created_at)}</time>
            <div className="acc-tl-card">
              <div className="acc-tl-body">
                <h3>{o.name || <span className="muted">Sản phẩm đã xóa</span>}</h3>
                <p className="acc-tl-meta">
                  Mã đơn <code>{o.trans_id}</code>
                </p>
                <div className="acc-tl-tags">
                  <StatusBadge status={o.status} />
                  <span className="acc-badge acc-badge-line">SL: {o.quantity}</span>
                </div>
              </div>
              <div className="acc-tl-side">
                <span className="acc-tl-total">{formatVnd(o.total)}</span>
                {orderAction(o)}
              </div>
            </div>
          </li>
        ))}
      </ol>
      <ReviewPager page={page} totalPages={totalPages} disabled={loading} onPage={setPage} />
    </>
  )
}

export function AccountQueuePage() {
  const { page, setPage, items, totalPages, loading } = usePaged<QueueRow>((p) =>
    api.accountQueue(p),
  )

  return (
    <>
      <div className="acc-head">
        <h2>Đơn đặt hộ</h2>
      </div>
      <Flash />
      {loading && !items.length ? <p className="muted">Đang tải…</p> : null}
      {!loading && !items.length ? <p className="muted">Chưa có đơn đặt hộ.</p> : null}
      {items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Mã đơn</th>
                <th>Sản phẩm</th>
                <th>Thời gian</th>
                <th>Ghi chú</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id}>
                  <td className="acc-nowrap">
                    <code>{q.code}</code>
                  </td>
                  <td className="acc-col-product">
                    {q.product_name}
                    {q.character_name ? (
                      <small className="acc-sub">
                        <i className="fa-solid fa-user" aria-hidden /> {q.character_name}
                      </small>
                    ) : null}
                  </td>
                  <td className="acc-col-time">{formatDateTime(q.created_at)}</td>
                  <td className="acc-col-note">
                    {q.note || (q.admin_note ? null : '—')}
                    {q.admin_note ? (
                      <small className="acc-sub acc-admin-note">
                        <i className="fa-solid fa-reply" aria-hidden /> {q.admin_note}
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge status={q.status} map={QUEUE_STATUS} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ReviewPager page={page} totalPages={totalPages} disabled={loading} onPage={setPage} />
    </>
  )
}

export function AccountNotificationsPage() {
  const [reload, setReload] = useState(0)
  const openItem = useOpenNotification()
  const { page, setPage, items, totalPages, loading } = usePaged<UserNotification>(
    (p) => api.notifications(p, 20),
    reload,
  )

  return (
    <>
      <div className="acc-head">
        <h2>Thông báo</h2>
        {items.some((n) => !n.read) ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              await api.readNotification('all').catch(() => null)
              setReload((n) => n + 1)
            }}
          >
            <i className="fa-solid fa-check-double" aria-hidden /> Đánh dấu đã đọc
          </button>
        ) : null}
      </div>
      {loading && !items.length ? <p className="muted">Đang tải…</p> : null}
      {!loading && !items.length ? <p className="muted">Chưa có thông báo.</p> : null}
      <ul className="acc-notifs">
        {items.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              className={`btn-reset acc-notif${n.read ? '' : ' unread'}${n.to ? ' link' : ''}`}
              onClick={async () => {
                await openItem(n)
                setReload((x) => x + 1)
              }}
            >
              <i className={`fa-solid ${n.read ? 'fa-envelope-open' : 'fa-envelope'}`} aria-hidden />
              <span>{n.text}</span>
              <time>{formatDateTime(n.created_at)}</time>
            </button>
          </li>
        ))}
      </ul>
      <ReviewPager page={page} totalPages={totalPages} disabled={loading} onPage={setPage} />
    </>
  )
}

export function AccountOtherPage() {
  const { user } = useAuth()
  const balance = usePaged<BalanceChange>((p) => api.balanceChanges(p))
  const [reviews, setReviews] = useState<MyReview[] | null>(null)

  useEffect(() => {
    api
      .myReviews()
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]))
  }, [])

  return (
    <>
      <div className="acc-head">
        <h2>Thông tin tài khoản</h2>
      </div>
      <dl className="acc-info">
        <div>
          <dt>Tên tài khoản</dt>
          <dd>{user?.name}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{user?.email}</dd>
        </div>
      </dl>

      <div className="acc-head acc-head-gap">
        <h2>Biến động số dư</h2>
      </div>
      {balance.loading && !balance.items.length ? <p className="muted">Đang tải…</p> : null}
      {!balance.loading && !balance.items.length ? (
        <p className="muted">Chưa có biến động.</p>
      ) : null}
      {balance.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nội dung</th>
                <th>Số tiền</th>
                <th>Số dư sau</th>
                <th>Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {balance.items.map((b) => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td className="acc-col-note">{b.note || '—'}</td>
                  <td className={b.kind === 'cong' ? 'acc-plus' : 'acc-minus'}>
                    {b.kind === 'cong' ? '+' : '−'}
                    {b.amount.toLocaleString('vi-VN')}
                  </td>
                  <td>{b.after.toLocaleString('vi-VN')}</td>
                  <td className="acc-nowrap">{formatDateTime(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ReviewPager
        page={balance.page}
        totalPages={balance.totalPages}
        disabled={balance.loading}
        onPage={balance.setPage}
      />

      <div className="acc-head acc-head-gap">
        <h2>Những đánh giá của bạn</h2>
      </div>
      {reviews === null ? <p className="muted">Đang tải…</p> : null}
      {reviews && !reviews.length ? <p className="muted">Chưa có đánh giá.</p> : null}
      <div className="acc-reviews">
        {reviews?.map((r) => (
          <article key={r.id} className="acc-review">
            <div className="acc-review-head">
              <StarsRow n={r.stars} />
              <StatusBadge status={r.status} map={REVIEW_STATUS} />
            </div>
            {r.product_name ? <p className="acc-review-product">{r.product_name}</p> : null}
            <p>{r.content}</p>
            {r.image_url ? <img src={r.image_url} alt="" loading="lazy" /> : null}
            {r.admin_reply ? (
              <div className="buykey-admin-reply">
                <span>
                  <i className="fa-solid fa-reply" aria-hidden /> Admin
                </span>
                <p>{r.admin_reply}</p>
              </div>
            ) : null}
            <time className="muted">{formatDateTime(r.created_at)}</time>
          </article>
        ))}
      </div>
    </>
  )
}
